/**
 * What a key binding is, and every action one can be put on.
 *
 * A binding maps one bindable action to the key codes that mean it, and the whole map is one
 * record so a conflict is a question the map can answer rather than a scan across files. Codes are
 * `KeyboardEvent.code`, never `key`: a code names a physical key, so a binding survives the player
 * switching layouts, and the label shown for it is resolved separately per layout.
 *
 * The list below is not `ActionId`. Devices report actions and keys separately, and three things
 * differ on the keyboard side: movement is four keys where a stick is an axis, the weapon row
 * binds by slot rather than by weapon so a sixth gun changes no map, and `revive` and `use` are
 * one physical key, `interact`, because they always were: which verb the hand means is the
 * simulation's to decide, and two rows for one key would invite a conflict with itself.
 */

export const BINDABLE_ACTIONS = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
  'sprint',
  'crouch',
  'jump',
  'fire',
  'aim',
  'reload',
  'inspect',
  'interact',
  'firstAid',
  'drill',
  'cycleView',
  'swapShoulder',
  'weaponSlot1',
  'weaponSlot2',
  'weaponSlot3',
  'weaponSlot4',
  'weaponSlot5',
  'weaponSlot6',
] as const;

/** The number row, in the order `WEAPON_CLASS_ORDER` gives the classes. Slot binds to class. */
export const WEAPON_SLOT_ACTIONS = [
  'weaponSlot1',
  'weaponSlot2',
  'weaponSlot3',
  'weaponSlot4',
  'weaponSlot5',
  'weaponSlot6',
] as const;

export type BindableActionId = (typeof BINDABLE_ACTIONS)[number];

export function isBindableActionId(value: string): value is BindableActionId {
  return BINDABLE_ACTIONS.some((action) => action === value);
}

/**
 * Every action's codes. A list per action, because the defaults offer synonyms, arrows beside
 * WASD; a player's own rebind replaces the list with the one code they pressed.
 */
export type BindingMap = Readonly<Record<BindableActionId, readonly string[]>>;
